import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { DispatchedServicesSyncDataDataService } from './dispatched-services-sync-data-data.service';
import { Router } from '@angular/router';
import { LoggerService } from 'src/app/telemetry/logger.service';
import { TelemetryIdentifiers } from 'src/app/telemetry/telemetry-identifiers';
import { ToastService } from 'src/app/toast/toast.service';
import { UserStateService } from 'src/app/user-state/user-state.service';
import { DriverRoutingConstants } from '../../pages/constants/driver-routing-constants';
import { DeleteAllSyncDataForDispatchedServiceCommandService } from '../commands/delete-all-sync-data-for-dispatched-service-command.service';
import { CurrentDispatchedServiceService } from '../state/current-dispatched-service.service';
import { DispatchedServiceViewModel } from '../../models/view-models/dispatched-service.view-model';

/**
 * This HTTP interceptor catches HTTP request errors for sync data when there is an orphaned service.
 */
@Injectable()
export class SyncDataInterceptor implements HttpInterceptor {

	/**
	 * @constructor
	 *
	 * @param deleteAllSyncDataForDispatchedServiceCommandService The service used for deleting all the sync data associated with a service.
	 * @param currentDispatchedServiceService The service used to get the current working service.
	 * @param toastService The toast service.
	 * @param logger The logger service.
	 * @param router Provides navigation and URL manipulation capabilities.
	 * @param userStateService The service that holds the user state.
	 */
	constructor(
		private readonly deleteAllSyncDataForDispatchedServiceCommandService: DeleteAllSyncDataForDispatchedServiceCommandService,
		private readonly currentDispatchedServiceService: CurrentDispatchedServiceService,
		private readonly toastService: ToastService,
		private readonly logger: LoggerService,
		private readonly router: Router,
		private readonly userStateService: UserStateService
	) { }

	/**
	 * Adds a header to the outgoing http request indicating the client version.
	 * 
	 * @param request The outgoing request object to handle.
	 * @param next The next interceptor in the chain, or the back-end if no interceptors remain in the chain.
	 */
	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(catchError((error: any) => {
			const syncRequest: string | null = request.headers.get(DispatchedServicesSyncDataDataService.SYNC_DATA_HEADER_NAME);
			if (!syncRequest)
				return throwError(() => error);

			if (error.status == HttpStatusCode.UnprocessableEntity)
			{
				const legacyId: number = Number(syncRequest);
				const toastMessage: string = `The service with id: ${legacyId} no longer exists. Deleted all sync data related to the service. Please click the refresh button on the Service Preview page to ensure your services are up to date.`;
				// Deletes all data related to the service, updates the next service to null
				let removeService = this.deleteAllSyncDataForDispatchedServiceCommandService.DeleteAllSyncDispatchedRecordsForAService$(legacyId).pipe(
					switchMap(() => {
						return this.currentDispatchedServiceService.GetCurrentService$().pipe(
							map((service: DispatchedServiceViewModel | null) => {
								if (service?.LegacyId == legacyId)
									{
										const currentUrl: string = `/${DriverRoutingConstants.ROUTES_BASE_FOLDER}/${DriverRoutingConstants.ROUTES_SERVICES_PREVIEW}`;
										this.router.navigate([currentUrl]).then(() => {
											this.toastService.ShowError(toastMessage);
										});
									}
									else
										this.toastService.ShowError(toastMessage);
							})
						);
					})
				);
				
				removeService.pipe(take(1)).subscribe({
					next: () => {
						this.logger.TrackEvent(TelemetryIdentifiers.EVENT_DELETE_ALL_DATA_FOR_ORPHANED_SERVICE, { LegacyId: legacyId });
					},
					error: (error: any) => {
						this.logger.TrackError("An error occurred removing the orphaned service.", error);
					}
				});
			}
			return throwError(() => error);
		}));
	}
}
